import { useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { enAU } from 'date-fns/locale';
import { addDays, addWeeks, format, isValid, nextMonday, parseISO, startOfDay } from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Cosmos's date picker: a compact trigger ("Thu 16 Oct") that opens a calendar with quick presets.
 * Use it for every editable date in the app instead of a native `<input type="date">`.
 *
 *  - `mode="date"` (default): value/onChange are calendar dates `YYYY-MM-DD` (project tasks, filters).
 *  - `mode="datetime"`: value/onChange are ISO instants (e.g. Autotask `Tickets.dueDateTime`); the
 *    popover adds a time select, and presets keep the current time of day (default 5:00 pm).
 *
 * Weeks start on Monday (en-AU). Times are shown in the browser's zone (Melbourne for Synfo staff).
 */

type Mode = 'date' | 'datetime';

export interface DatePickerProps {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  mode?: Mode;
  placeholder?: string;
  disabled?: boolean;
  /** Offer "Clear" (onChange(null)). Off for fields Autotask won't let you blank. */
  clearable?: boolean;
  /** Paint the trigger as overdue / at risk. */
  tone?: 'default' | 'danger' | 'warn';
  size?: 'sm' | 'md';
  /** Earliest / latest selectable calendar day (YYYY-MM-DD). */
  min?: string;
  max?: string;
  /** Show the quick presets column (Today, Tomorrow, Next Monday, +1 week, +2 weeks). */
  presets?: boolean;
  className?: string;
  align?: 'start' | 'center' | 'end';
  'aria-label'?: string;
}

const DEFAULT_HOUR = 17;
const TIMES = Array.from({ length: 24 * 4 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`);

function parseValue(value: string | null | undefined, mode: Mode): Date | null {
  if (!value) return null;
  const d = mode === 'date' ? parseISO(value.slice(0, 10)) : parseISO(value);
  return isValid(d) ? d : null;
}

function emit(day: Date, mode: Mode, time: string): string {
  if (mode === 'date') return format(day, 'yyyy-MM-dd');
  const [h, m] = time.split(':').map(Number);
  const d = new Date(day);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

function label(d: Date, mode: Mode) {
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const day = format(d, sameYear ? 'EEE d MMM' : 'd MMM yyyy', { locale: enAU });
  return mode === 'datetime' ? `${day}, ${format(d, 'h:mm a', { locale: enAU }).toLowerCase()}` : day;
}

export function DatePicker({
  value, onChange, mode = 'date', placeholder = 'Set date', disabled, clearable = true, tone = 'default', size = 'sm',
  min, max, presets = true, className, align = 'start', ...rest
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseValue(value, mode);
  const time = selected && mode === 'datetime' ? format(selected, 'HH:mm') : `${DEFAULT_HOUR}:00`;
  const [month, setMonth] = useState<Date>(selected ?? new Date());
  const minD = min ? parseISO(min) : undefined;
  const maxD = max ? parseISO(max) : undefined;

  const pick = (day: Date | undefined, t = time) => {
    if (!day) return;
    onChange(emit(day, mode, t));
    if (mode === 'date') setOpen(false);
  };

  const quick = useMemo(() => {
    const today = startOfDay(new Date());
    return [
      { label: 'Today', day: today },
      { label: 'Tomorrow', day: addDays(today, 1) },
      { label: 'Next Monday', day: nextMonday(today) },
      { label: 'In 1 week', day: addWeeks(today, 1) },
      { label: 'In 2 weeks', day: addWeeks(today, 2) },
    ].filter((q) => (!minD || q.day >= startOfDay(minD)) && (!maxD || q.day <= maxD));
  }, [min, max]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setMonth(selected ?? new Date()); }}>
      <PopoverTrigger asChild disabled={disabled}>
        <button type="button" aria-label={rest['aria-label'] ?? 'Choose date'} onClick={(e) => e.stopPropagation()}
          className={cn(
            'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border bg-background text-left tabular-nums transition-colors',
            'hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            size === 'sm' ? 'h-7 px-2 text-xs' : 'h-9 px-3 text-sm',
            tone === 'danger' ? 'border-destructive/50 text-destructive' : tone === 'warn' ? 'border-warning/50 text-warning' : 'border-input text-foreground',
            !selected && 'text-muted-foreground',
            className,
          )}>
          <CalendarDays className={cn('shrink-0 opacity-70', size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
          <span>{selected ? label(selected, mode) : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0" onClick={(e) => e.stopPropagation()}>
        <div className="flex">
          {presets && quick.length > 0 && (
            <div className="flex w-32 flex-col gap-0.5 border-r border-border p-2">
              {quick.map((q) => (
                <button key={q.label} type="button" onClick={() => pick(q.day)}
                  className="rounded-md px-2 py-1.5 text-left text-xs text-foreground hover:bg-accent">
                  {q.label}
                  <span className="block text-[10.5px] text-muted-foreground">{format(q.day, 'EEE d MMM', { locale: enAU })}</span>
                </button>
              ))}
            </div>
          )}
          <div className="p-3">
            <DayPicker
              mode="single"
              locale={enAU}
              weekStartsOn={1}
              showOutsideDays
              month={month}
              onMonthChange={setMonth}
              selected={selected ?? undefined}
              onSelect={(d) => pick(d)}
              disabled={[...(minD ? [{ before: minD }] : []), ...(maxD ? [{ after: maxD }] : [])]}
              components={{
                Chevron: ({ orientation }) => (orientation === 'left' ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />),
              }}
              classNames={{
                root: 'relative',
                months: 'flex flex-col',
                month: 'space-y-2',
                month_caption: 'flex h-8 items-center justify-center',
                caption_label: 'text-sm font-semibold text-foreground',
                nav: 'absolute inset-x-0 top-0 flex h-8 items-center justify-between',
                button_previous: 'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30',
                button_next: 'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30',
                month_grid: 'border-collapse',
                weekdays: 'flex',
                weekday: 'w-9 pb-1 text-center text-[11px] font-medium text-muted-foreground',
                week: 'flex',
                day: 'h-9 w-9 p-0 text-center text-sm',
                day_button: 'h-9 w-9 rounded-md tabular-nums text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                selected: '[&>button]:bg-primary [&>button]:font-semibold [&>button]:text-primary-foreground [&>button]:hover:bg-primary',
                today: '[&>button]:font-semibold [&>button]:text-primary [&>button]:underline [&>button]:underline-offset-4',
                outside: '[&>button]:text-muted-foreground/50',
                disabled: '[&>button]:pointer-events-none [&>button]:opacity-30',
                hidden: 'invisible',
              }}
            />
            {(mode === 'datetime' || (clearable && selected)) && (
              <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
                {mode === 'datetime' && (
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    Time
                    <select value={time} onChange={(e) => pick(selected ?? startOfDay(new Date()), e.target.value)}
                      className="h-7 rounded-md border border-input bg-background px-1.5 text-xs text-foreground">
                      {!TIMES.includes(time) && <option value={time}>{time}</option>}
                      {TIMES.map((t) => <option key={t} value={t}>{format(parseISO(`2000-01-01T${t}`), 'h:mm a').toLowerCase()}</option>)}
                    </select>
                  </label>
                )}
                {clearable && selected && (
                  <button type="button" onClick={() => { onChange(null); setOpen(false); }}
                    className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-destructive">
                    <X className="h-3 w-3" /> Clear
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
